class ProductFormComponent extends HTMLElement {
  constructor() {
    super();

    // Listen on `this` so the listener works even when children aren't available at construct time
    // (e.g. when this element is created via innerHTML replacement during cart drawer re-render)
    this.addEventListener('submit', this.onSubmitHandler.bind(this));
    this.addEventListener('change', this.onUpsellChangeHandler.bind(this));
    this.cartNotification = document.querySelector('cart-notification');
    this.cartDrawer = document.querySelector('cart-drawer');
    this.cartItems = document.querySelector('cart-items');

    this.isCartPage = !!document.body.classList.contains('template-cart');
  }

  // Update the price in the add to cart button when upsell products are (de)selected
  onUpsellChangeHandler(event) {
    if (!event.target.classList.contains('product-form__upsell-input')) return;

    // Paste packs: the paste form calculates the pack price plus the checked upsell products
    const pasteForm = this.querySelector('paste-form');
    if (pasteForm) {
      pasteForm.updatePrice();
      return;
    }

    const priceElement = this.querySelector('[data-base-price]');
    if (!priceElement) return;

    const priceTextElement = priceElement.querySelector('.price__item--regular, .price__item--sale');
    if (!priceTextElement) return;

    const basePrice = parseInt(priceElement.dataset.basePrice, 10) || 0;
    const checkedUpsellInputs = this.querySelectorAll('input.product-form__upsell-input:checked');
    let totalPrice = basePrice;
    checkedUpsellInputs.forEach(upsellInput => {
      totalPrice += parseInt(upsellInput.dataset.upsellPrice, 10) || 0;
    });

    priceTextElement.textContent = Shopify.formatMoney(totalPrice).replace(',00', ',-');
  }

  async onSubmitHandler(event, trigger = null) {
    if (event) event.preventDefault();

    const form = this.querySelector('form');
    if (!form) return;

    const submitButton = this.querySelector('[type="submit"]');
    submitButton.setAttribute('disabled', true);
    submitButton.classList.add('button--loading');
    this.showError('');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    let sectionsToFetch = [];
    if (this.cartDrawer) {
      sectionsToFetch = [...sectionsToFetch, ...this.cartDrawer.getSectionsToRender().map(section => section.section)];
    }
    else if (this.isCartPage) {
      sectionsToFetch = [
        ...sectionsToFetch,
        ...this.cartItems.getCartSectionsToRender().map(section => section.section),
        ...this.cartItems.getCartRelatedSection().map(section => section.section),
      ];
    }
    if (this.cartNotification && !this.isCartPage) {
      sectionsToFetch = [...sectionsToFetch, ...this.cartNotification.getSectionsToRender().map(section => section.section)];
    }

    let uniqueSectionsToFetch = [...new Set(sectionsToFetch)];

    const formData = new FormData(form);
    const variantHasRepeat = formData.get('repeat');
    const sellingPlan = formData.get('selling_plan');

    let items;
    let freeGiftInputs = [];
    let freeGiftQuantity = 1;

    const pasteForm = this.querySelector('paste-form');
    if (pasteForm) {
      // Paste pack: add each selected flavor as a separate cart item
      const ids = pasteForm.getSelectedVariantIds();
      if (!ids.length) {
        submitButton.removeAttribute('disabled');
        submitButton.classList.remove('button--loading');
        return;
      }

      const groupId = crypto.randomUUID();
      const packSize = ids.length;
      const checkedPackInput = pasteForm.querySelector('input[name="paste-pack"]:checked');
      const packName = checkedPackInput?.dataset.packName ?? `${packSize}-pack`;
      // The pack discount is applied by the discount app in checkout, the cart needs it to show the right totals
      const packDiscountPerItem = parseInt(checkedPackInput?.dataset.packDiscountPerItem, 10) || 0;

      // Pack items are always a one-time purchase: the selling_plan form input (blocks/_product-form.liquid,
      // sections/theme-quick-shop.liquid) is ignored here, even when the selected variant has a selling plan
      items = ids.map((id, index) => {
        const variantData = pasteForm.variantsData.find(variantDataItem => variantDataItem.id == id);
        return {
          id: variantData?.cartVariantId ?? parseInt(id, 10),
          quantity: '1',
          properties: {
            'Pack size': packName,
            _pasteGroup: groupId,
            _packSize: packSize,
            _packName: packName,
            _packType: pasteForm.packTypeHandle,
            _packDiscountPerItem: packDiscountPerItem,
            _slot: index + 1,
            _hasRepeat: 'false',
          },
        };
      });
    }
    else {
      // Regular product
      const id = formData.get('id');
      const quantity = formData.get('quantity');
      const productTitle = formData.get('product-title');

      items = [
        {
          id,
          quantity,
          properties: {
            _hasRepeat: variantHasRepeat,
          },
        },
      ];

      // repeat abo product
      if (variantHasRepeat === 'true') {
        items[0].properties._repeatProduct = id;
        items.push({
          id: window.repeatProduct.repeat,
          quantity,
          selling_plan: window.repeatProduct.repeat_selling_plan,
          parent_id: id,
          properties: {
            _repeatProduct: id,
            _repeatProductTitle: productTitle,
          },
        });
        if (sellingPlan) {
          items[1].selling_plan = window.repeatProduct.repeat_selling_plan;
        }
      }
      else if (sellingPlan) {
        items[0].selling_plan = sellingPlan;
      }

      // Free gifts that come with the product (snippets/free-gifts.liquid), added in the same call
      freeGiftInputs = form.querySelectorAll('input.product-form__free-gift-input');
      freeGiftQuantity = parseInt(quantity, 10) || 1;
    }

    // Upsell products that are added along with the main product or the paste pack
    const upsellInputs = form.querySelectorAll('input.product-form__upsell-input:checked');
    upsellInputs.forEach(input => {
      const variantId = input.value ? parseInt(input.value, 10) : 0;
      if (variantId) {
        items.push({
          id: variantId,
          quantity: 1,
        });
      }
    });

    if (freeGiftInputs.length) {
      items.push(...await this.getFreeGiftItems(freeGiftInputs, freeGiftQuantity));
    }

    const body = JSON.stringify({
      ...{ items },
      sections: uniqueSectionsToFetch,
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(),
      ...{ body } })
      .then(response => response.json())
      .then(parsedState => {
        if (parsedState.status) {
          // Render the cart drawer items again because even if there was a quantity error, items could still have been added
          this.cartDrawer?.renderCartDrawer();
          this.showError(parsedState.description || window.cartStrings.error);
          return;
        }

        if (this.cartDrawer) {
          if (parsedState.sections) {
            this.cartDrawer.renderContents(parsedState);
          }
          else {
            this.cartDrawer.renderCartDrawer();
          }
        }
        else {
          // on the cart page
          this.cartItems.updateContent(parsedState, null, null, true);
          // The empty state hides the items and footer (main-cart.liquid), the cart has items now
          this.cartItems.classList.remove('cart-is-empty');
        }

        window.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: parsedState }));

        switch (submitButton.dataset.addToCartBehavior) {
        case 'open_cart_drawer':
          // Set timeout to force animation. Because content is just updated with renderContents
          setTimeout(() => {
            this.cartDrawer?.open(trigger);
          }, 100);
          break;
        case 'show_cart_notification':
          if (!this.isCartPage && !document.body.classList.contains('cart-drawer-is-open')) {
            this.cartNotification?.renderContents(parsedState, submitButton);
          }
          break;
        default:
          this.cartNotification?.renderContents(parsedState, submitButton);
        }
      })
      .catch(error => {
        debug() && console.error(error);
        this.showError(window.cartStrings.error);
      })
      .finally(() => {
        submitButton.classList.remove('button--loading');
        submitButton.removeAttribute('disabled');

        if (this.closest('#addToCartModal')) {
          document.body.classList.remove('show-add-to-cart-modal');
          removePreventScroll();
        }
      });
  }

  /**
   * Shows the add to cart error below the submit button, an empty message clears it.
   * The quick shop dialog closes on submit, so it is opened again to show the message.
   * @param {String} message
   */
  showError(message) {
    const errorElement = this.querySelector('[data-product-form-error]');
    if (!errorElement) return;

    errorElement.textContent = message;

    const dialog = this.closest('dialog');
    if (message && dialog && !dialog.open) dialog.showModal();
  }

  /**
   * The gift lines to add together with the product: the gifts of the product form minus what is already in the cart,
   * capped at the maximum of the gift. The gift lines in the cart come from the last rendered cart markup (free-gifts.js),
   * so no request is needed; the reconcile in free-gifts.js corrects the cart afterwards when that was out of date.
   * @param {NodeList} inputs the hidden gift inputs (snippets/free-gifts.liquid)
   * @param {Number} quantity the quantity of the product being added
   * @returns {Array} items for /cart/add.js
   */
  async getFreeGiftItems(inputs, quantity) {
    let giftLines = [];
    if (window.FreeGifts) {
      giftLines = window.FreeGifts.getGiftLines();
    }
    else {
      // No cart drawer or cart page on this page: read the cart
      try {
        const response = await fetch(routes.cart_url);
        if (response.ok) {
          giftLines = ((await response.json()).items || [])
            .filter(item => item.properties?._free_gift)
            .map(item => ({ handle: item.properties._free_gift, quantity: item.quantity }));
        }
      }
      catch (error) {
        debug() && console.error('Error fetching the cart for the free gifts.', error);
      }
    }

    const giftItems = [];
    inputs.forEach(input => {
      const variantId = parseInt(input.value, 10);
      const handle = input.dataset.freeGiftHandle;
      const maxQuantity = parseInt(input.dataset.freeGiftMax, 10) || 0;
      if (!variantId || !handle) return;

      const quantityInCart = giftLines
        .filter(line => line.handle === handle)
        .reduce((sum, line) => sum + line.quantity, 0);

      let wantedQuantity = input.dataset.freeGiftMode === 'per_cart' ? 1 : quantityInCart + quantity;
      if (maxQuantity > 0) wantedQuantity = Math.min(wantedQuantity, maxQuantity);

      if (wantedQuantity > quantityInCart) {
        giftItems.push({
          id: variantId,
          quantity: wantedQuantity - quantityInCart,
          properties: {
            _free_product: true,
            _free_gift: handle,
          },
        });
      }
    });
    return giftItems;
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}
